/**
 * Inferences page (formerly Forecasts).
 *
 * Lists every inference run produced by the workspace's deployments,
 * using the unified DataTable so the look-and-feel matches every
 * other listings page.
 *
 * The "Run Inference" button opens a modal that mirrors the Segment
 * Inference modal on the deployment-detail page: pick a deployment,
 * a segment, and a horizon, then dispatch the existing
 * ``runSegmentInference`` saga.
 *
 * URL: ``/inference`` (kept for backwards-compat with old links).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { format } from '../../utils/datetime';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, FormControl, Grid,
  InputLabel, MenuItem, Paper, Select, Stack, TextField, Tooltip,
  Typography, alpha, useTheme,
} from '@mui/material';
import { InferencesIcon, SegmentsIcon } from '../global/CustomIcons';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LaunchIcon from '@mui/icons-material/Launch';
import SearchIcon from '@mui/icons-material/Search';
import InferenceMiniChart, { predictionFinal } from './InferenceMiniChart';
import { useSignalUnits } from '../../utils/units';
import { CMButton } from '../../containers/styledComponents';
import { fetchRequest as fetchInferences } from '../../redux/actions/inferences/list';
import { fetchRequest as fetchDeployments } from '../../redux/actions/deployments/list';
import { fetchRequest as fetchSegments } from '../../redux/actions/segments/list';
import {
  segmentInferenceRequest as runSegmentInference,
  clearInferenceState,
} from '../../redux/actions/deployments/inference';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import DataTable from '../common/DataTable';
import ListingToolbar from '../common/ListingToolbar';
import EntityCell from '../common/EntityCell';
import RowActions from '../common/RowActions';
import useRealtimeRefresh, { eventPrefix } from '../../realtime/useRealtimeRefresh';

const formatDate = (s) => {
  if (!s) return '—';
  try { return format(new Date(s), 'MMM d, HH:mm:ss'); } catch { return String(s); }
};

const fmtFinal = (v) => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(v / 1e3).toFixed(1)}k`;
  return v.toFixed(abs >= 1 ? 1 : 3);
};

const severityColor = (severity, theme) => ({
  success: theme.palette.success.main,
  primary: theme.palette.primary.main,
  warning: theme.palette.warning.main,
  error:   theme.palette.error.main,
  neutral: theme.palette.text.primary,
}[severity] || theme.palette.text.primary);

// --- Run Inference modal ---------------------------------------------------

const RunInferenceDialog = ({
  open, onClose, onSubmit,
  deployments, deploymentsLoading,
  segments, segmentsLoading,
  inferenceLoading, inferenceError, inferenceData,
  resetInferenceState,
}) => {
  const theme = useTheme();
  const [deploymentId, setDeploymentId] = useState('');
  const [segmentId, setSegmentId] = useState('');
  const [horizon, setHorizon] = useState('64');

  const selectedDeployment = useMemo(
    () => (deployments || []).find((d) => d.promotion_id === deploymentId),
    [deployments, deploymentId],
  );

  useEffect(() => {
    if (!open || deploymentId) return;
    const first = (deployments || []).find((d) => d.ready) || (deployments || [])[0];
    if (first) setDeploymentId(first.promotion_id);
  }, [open, deployments, deploymentId]);

  const close = () => {
    resetInferenceState();
    setDeploymentId(''); setSegmentId(''); setHorizon('64');
    onClose();
  };

  const submit = () => {
    if (!deploymentId || !segmentId) return;
    onSubmit({
      deployment_id: deploymentId,
      segment_id: segmentId,
      horizon: parseInt(horizon, 10),
    });
  };

  const familySlug = selectedDeployment
    ? (selectedDeployment.foundation_family
      ? `${selectedDeployment.foundation_family} / ${selectedDeployment.foundation_slug}`
      : selectedDeployment.promotion_id)
    : '';

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <SegmentsIcon sx={{ mr: 1 }} />
        Segment Inference
      </DialogTitle>
      <DialogContent>
        {inferenceLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              Running segment inference...
            </Typography>
          </Box>
        ) : inferenceError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>Inference Failed</Typography>
            <Typography variant="body2">
              {typeof inferenceError === 'object'
                ? inferenceError.message || 'Unknown error'
                : inferenceError}
            </Typography>
          </Alert>
        ) : inferenceData ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Inference completed in {Math.round(inferenceData?.performance?.latency_ms ?? inferenceData?.execution_time_ms ?? 0)}ms.
            Open it from the list below to see the full forecast and table.
          </Alert>
        ) : (
          <Box>
            <Typography variant="subtitle2" gutterBottom>Deployment</Typography>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Pick a deployment</InputLabel>
              <Select
                label="Pick a deployment"
                value={deploymentId}
                onChange={(e) => setDeploymentId(e.target.value)}
                disabled={deploymentsLoading}
              >
                {(deployments || []).length === 0 ? (
                  <MenuItem value="" disabled>
                    {deploymentsLoading ? 'Loading deployments...' : 'No deployments in this workspace'}
                  </MenuItem>
                ) : (deployments || []).map((d) => (
                  <MenuItem key={d.promotion_id} value={d.promotion_id}>
                    {d.foundation_family
                      ? `${d.foundation_family} / ${d.foundation_slug}`
                      : d.promotion_id}
                    {!d.ready && ' · (not ready)'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedDeployment && (
              <>
                <Typography variant="subtitle2" gutterBottom>Foundation model</Typography>
                <Paper sx={{ p: 2, mb: 2, backgroundColor: theme.palette.action.hover }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Family / Slug
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>{familySlug}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Mode</Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {selectedDeployment.foundation_adapter_id
                          ? 'Fine-tuned (LoRA adapter attached)'
                          : 'Zero-shot (no adapter)'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>

                <Typography variant="subtitle2" gutterBottom>Forecast Inputs</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={8}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Segment to forecast</InputLabel>
                      <Select
                        label="Segment to forecast"
                        value={segmentId}
                        onChange={(e) => setSegmentId(e.target.value)}
                        disabled={segmentsLoading}
                      >
                        {(segments || []).length === 0 ? (
                          <MenuItem value="" disabled>
                            {segmentsLoading ? 'Loading segments...' : 'No segments in this workspace'}
                          </MenuItem>
                        ) : (segments || []).map((s) => (
                          <MenuItem key={s.uid} value={s.uid}>
                            {s.name || s.uid}
                            {s.labels?.length
                              ? ` (${s.labels.length} label${s.labels.length > 1 ? 's' : ''})`
                              : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Horizon</InputLabel>
                      <Select
                        label="Horizon"
                        value={horizon}
                        onChange={(e) => setHorizon(e.target.value)}
                      >
                        {[16, 32, 64, 128, 256, 512].map((h) => (
                          <MenuItem key={h} value={h.toString()}>{h} steps</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                <Typography variant="caption" color="text.secondary"
                  sx={{ display: 'block', mt: 1 }}>
                  The model consumes raw values from the segment&apos;s declared labels.
                  A trailing context window of ~4x the horizon is pulled from the segment&apos;s recent data.
                </Typography>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!inferenceData && !inferenceLoading && (
          <Button
            variant="contained"
            startIcon={<LaunchIcon />}
            onClick={submit}
            disabled={!deploymentId || !segmentId || inferenceLoading}
          >
            {inferenceLoading ? <CircularProgress size={20} /> : 'Run Inference'}
          </Button>
        )}
        <Button onClick={close}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Page ------------------------------------------------------------------

const InferencesPage = ({
  inferences, historyBySegment, isLoading, error, pagination,
  deployments, deploymentsLoading,
  segments, segmentsLoading,
  inferenceData, inferenceLoading, inferenceError,
  loadInferences, loadDeployments, loadSegments,
  triggerInference, resetInferenceState,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  // Measurement units per signal key (labels are signal keys).
  const unitOf = useSignalUnits();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [deploymentFilter, setDeploymentFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]); // 'success' | 'failed'
  const [dialogOpen, setDialogOpen] = useState(false);

  const workspaceId = typeof window !== 'undefined'
    ? (localStorage.getItem('WORKSPACE_ID') || '') : '';

  // Pull deployments + segments for the filter / dialog dropdowns.
  useEffect(() => {
    if (workspaceId) loadDeployments({ workspace_id: workspaceId, page: 1, limit: 1000 });
    else           loadDeployments({ page: 1, limit: 1000 });
    loadSegments();
  }, [loadDeployments, loadSegments, workspaceId]);

  const deploymentFilterKey = deploymentFilter.join(',');

  const refresh = useCallback(() => {
    const params = { page: page + 1, limit: rowsPerPage };
    if (deploymentFilter.length > 0) params.deployment_id = deploymentFilter[0];
    loadInferences(params);
  }, [loadInferences, page, rowsPerPage, deploymentFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { setPage(0); }, [deploymentFilterKey, search, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (inferenceData) refresh(); }, [inferenceData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live (no polling): a new inference (scheduled deployment, API/bot, or
  // another user) lands on the workspace channel -> refresh the list.
  useRealtimeRefresh(eventPrefix('inference.'), refresh);

  const openDialog = () => { resetInferenceState(); setDialogOpen(true); };
  const closeDialog = () => { setDialogOpen(false); resetInferenceState(); };
  const goToDetail = (uid) => navigate(`/inferences/${uid}`);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (inferences || []).filter((inf) => {
      const ok = inf.response?.success !== false && !inf.response?.error;
      if (statusFilter.length > 0) {
        const s = ok ? 'success' : 'failed';
        if (!statusFilter.includes(s)) return false;
      }
      if (!q) return true;
      return (inf.deployment_label || '').toLowerCase().includes(q)
        || (inf.deployment_id || '').toLowerCase().includes(q)
        || (inf.segment_name || '').toLowerCase().includes(q)
        || ((inf.segment_metadata?.segment_id) || '').toLowerCase().includes(q)
        || (inf.uid || '').toLowerCase().includes(q);
    });
  }, [inferences, search, statusFilter]);

  const selectedDeploymentObjs = useMemo(
    () => (deployments || []).filter((d) => deploymentFilter.includes(d.promotion_id)),
    [deployments, deploymentFilter],
  );

  const toggleStatus = (s) =>
    setStatusFilter((prev) => prev.includes(s) ? prev.filter((v) => v !== s) : [...prev, s]);

  const activeFilterCount =
    (search ? 1 : 0)
    + (deploymentFilter.length > 0 ? 1 : 0)
    + (statusFilter.length > 0 ? 1 : 0);

  const clearAll = () => {
    setSearch('');
    setDeploymentFilter([]);
    setStatusFilter([]);
  };

  const columns = [
    {
      key: 'identity',
      label: 'ID',
      width: 130,
      render: (inf) => {
        const ok = inf.response?.success !== false && !inf.response?.error;
        const latency = Math.round(inf.performance?.latency_ms ?? 0);
        return (
          <EntityCell
            id={inf.uid}
            tone={ok ? 'success' : 'error'}
            label={ok ? `${latency}ms` : 'Failed'}
          />
        );
      },
    },
    {
      key: 'timestamp',
      label: 'Date',
      width: 150,
      render: (inf) => (
        <Tooltip title={formatDate(inf.timestamp)} arrow placement="top">
          <Typography variant="body2" sx={{ fontSize: 13, lineHeight: 1.3 }}>
            {formatDate(inf.timestamp)}
          </Typography>
        </Tooltip>
      ),
    },
    {
      key: 'deployment',
      label: 'Deployment',
      width: '20%',
      render: (inf) => (
        <Box>
          <Tooltip title={inf.deployment_id || ''} arrow placement="top">
            <Typography variant="body2" sx={{
              fontSize: 13, fontWeight: 500,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              maxWidth: 200,
            }}>
              {inf.deployment_label || inf.deployment_id || '—'}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    {
      key: 'segment',
      label: 'Segment',
      width: '20%',
      render: (inf) => (
        <Box>
          <Typography variant="body2" sx={{
            fontSize: 13,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            maxWidth: 200,
          }}>
            {inf.segment_name || (inf.segment_metadata || {}).segment_id || '—'}
          </Typography>
          {(inf.segment_metadata || {}).labels_used?.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{
              display: 'block', fontSize: 11,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {(inf.segment_metadata || {}).labels_used.join(', ')}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'horizon',
      label: 'Horizon',
      width: 80,
      align: 'right',
      render: (inf) => {
        const pred = (inf.response?.predictions || [])[0];
        const horizon = (inf.segment_metadata || {}).horizon ?? (pred?.point?.length ?? '—');
        return (
          <Typography variant="body2" sx={{
            fontSize: 13, fontVariantNumeric: 'tabular-nums',
            color: theme.palette.text.secondary,
          }}>
            {horizon}{typeof horizon === 'number' ? ' st' : ''}
          </Typography>
        );
      },
    },
    {
      key: 'final',
      label: 'Final',
      width: 100,
      align: 'right',
      render: (inf) => {
        const pred = (inf.response?.predictions || [])[0];
        const final = predictionFinal(pred);
        return (
          <Tooltip
            title={final.bandPct !== null
              ? `80% interval is ±${final.bandPct.toFixed(1)}% of p50`
              : 'No quantile band available'}
            arrow placement="top"
          >
            <Box>
              <Typography variant="body2" sx={{
                fontWeight: 700,
                color: severityColor(final.severity, theme),
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1.1,
                fontSize: 13,
              }}>
                {fmtFinal(final.value)}
              </Typography>
              {final.bandPct !== null && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  ±{final.bandPct.toFixed(1)}%
                </Typography>
              )}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      key: 'forecast',
      label: 'Forecast',
      width: 170,
      render: (inf) => {
        const pred = (inf.response?.predictions || [])[0];
        const segId = (inf.segment_metadata || {}).segment_id;
        const segHistory = historyBySegment[segId];
        const labelsUsed = (inf.segment_metadata || {}).labels_used || [];
        const histLabel = pred?.label || labelsUsed[0];
        const histSeries = segHistory?.labels?.[histLabel];
        const historyPts = histSeries
          ? histSeries.timestamps
              .map((t, i) => ({ x: t, y: histSeries.values[i] }))
              .filter((p) => p.y !== null && p.y !== undefined)
          : [];
        const intervalSec = segHistory?.interval_seconds || 60;
        return (
          <Box sx={{ height: 44, minWidth: 150 }}>
            <InferenceMiniChart
              prediction={pred}
              history={historyPts}
              intervalSec={intervalSec}
              unit={unitOf(histLabel)}
            />
          </Box>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      width: 60,
      align: 'right',
      stopRowClick: true,
      render: (inf) => (
        <RowActions onOpen={() => goToDetail(inf.uid)} openLabel="Open inference" />
      ),
    },
  ];

  if (error) {
    return (
      <Box sx={{ width: '100%' }}>
        <PageBreadcrumbs items={[{ text: 'Inferences', icon: <InferencesIcon /> }]} sx={{ mb: 2 }} />
        <Box sx={{ p: 3, bgcolor: alpha(theme.palette.error.main, 0.08), color: theme.palette.error.main, borderRadius: 1 }}>
          <Typography variant="h6">Failed to load inferences</Typography>
          <Typography variant="body2">{String(error)}</Typography>
        </Box>
      </Box>
    );
  }

  const filtersNode = (
    <>
      <TextField
        size="small"
        placeholder="Search inferences…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: <SearchIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />,
        }}
        sx={{ minWidth: 220, flex: 1 }}
      />
      <Autocomplete
        multiple
        size="small"
        options={deployments || []}
        value={selectedDeploymentObjs}
        onChange={(_, val) => setDeploymentFilter(val.map((v) => v.promotion_id))}
        getOptionLabel={(d) => {
          if (!d) return '';
          if (d.foundation_family) {
            return `${d.foundation_family}/${d.foundation_slug || ''}`;
          }
          return d.model_name || d.promotion_id;
        }}
        isOptionEqualToValue={(a, b) => a.promotion_id === b.promotion_id}
        renderInput={(params) => (
          <TextField {...params} label="Deployment" placeholder={selectedDeploymentObjs.length ? '' : 'Any'} />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.promotion_id}
              label={option.foundation_slug
                || option.model_name
                || option.promotion_id}
              size="small"
            />
          ))
        }
        sx={{ minWidth: 220, flex: 1 }}
        limitTags={1}
        disabled={deploymentsLoading}
      />
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography variant="caption" color="text.secondary" sx={{
          fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', mr: 0.5,
        }}>
          Status
        </Typography>
        {[
          { value: 'success', label: 'Success', color: 'success' },
          { value: 'failed',  label: 'Failed',  color: 'error'   },
        ].map((opt) => {
          const selected = statusFilter.includes(opt.value);
          return (
            <Chip
              key={opt.value}
              label={opt.label}
              size="small"
              onClick={() => toggleStatus(opt.value)}
              color={selected ? opt.color : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer' }}
            />
          );
        })}
      </Stack>
    </>
  );

  const actionsNode = (
    <CMButton startIcon={<PlayArrowIcon />} onClick={openDialog} size="small">
      Run Inference
    </CMButton>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <PageBreadcrumbs items={[{ text: 'Inferences', icon: <InferencesIcon /> }]} mb={0.5} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, ml: 1 }}>
        Every inference run produced by your deployments. Click a row for the full forecast and table.
      </Typography>

      <ListingToolbar
        filters={filtersNode}
        actions={actionsNode}
        activeCount={activeFilterCount}
        onClearFilters={clearAll}
      />

      <DataTable
        columns={columns}
        rows={filteredRows}
        getRowKey={(row) => row.uid}
        onRowClick={(row) => goToDetail(row.uid)}
        loading={isLoading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalItems={pagination?.total || 0}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => { setRowsPerPage(n); setPage(0); }}
        rowsPerPageOptions={[10, 20, 50, 100]}
        emptyState={{
          icon: <InferencesIcon sx={{ fontSize: 32 }} />,
          title: activeFilterCount > 0 ? 'No inferences match your filters' : 'No Inferences Yet',
          message: activeFilterCount > 0
            ? 'No inferences match your current filters.'
            : "You haven't run any inferences yet.",
          action: activeFilterCount > 0 ? null : (
            <CMButton startIcon={<PlayArrowIcon />} onClick={openDialog}>
              Run Your First Inference
            </CMButton>
          ),
        }}
      />

      <RunInferenceDialog
        open={dialogOpen}
        onClose={closeDialog}
        onSubmit={triggerInference}
        deployments={deployments}
        deploymentsLoading={deploymentsLoading}
        segments={segments}
        segmentsLoading={segmentsLoading}
        inferenceData={inferenceData}
        inferenceLoading={inferenceLoading}
        inferenceError={inferenceError}
        resetInferenceState={resetInferenceState}
      />
    </Box>
  );
};

const mapStateToProps = (state) => ({
  inferences:         state.inferences_list.response?.inferences || [],
  historyBySegment:   state.inferences_list.response?.history_by_segment || {},
  isLoading:          state.inferences_list.isLoading,
  error:              state.inferences_list.error,
  pagination:         state.inferences_list.response?.pagination,
  deployments:        state.deployments_list?.response?.deployments
                       || state.deployments_list?.response?.results
                       || [],
  deploymentsLoading: state.deployments_list?.isLoading || false,
  segments:           state.segments_list?.response?.segments || [],
  segmentsLoading:    state.segments_list?.isLoading || false,
  inferenceData:      state.segment_inference?.data,
  inferenceLoading:   state.segment_inference?.loading,
  inferenceError:     state.segment_inference?.error,
});

const mapDispatchToProps = (dispatch) => ({
  loadInferences:      (params) => dispatch(fetchInferences(params)),
  loadDeployments:     (params) => dispatch(fetchDeployments(params)),
  loadSegments:        ()       => dispatch(fetchSegments({})),
  triggerInference:    (payload) => dispatch(runSegmentInference(payload)),
  resetInferenceState: ()       => dispatch(clearInferenceState()),
});

export default connect(mapStateToProps, mapDispatchToProps)(InferencesPage);
